"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.selectHorizontalScrollTokens = exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _Platform = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Platform"));
var _StyleSheet = _interopRequireDefault(require("react-native-web/dist/cjs/exports/StyleSheet"));
var _View = _interopRequireDefault(require("react-native-web/dist/cjs/exports/View"));
var _ThemeProvider = require("../ThemeProvider");
var _utils = require("../utils");
var _ScrollViewEnd = _interopRequireDefault(require("./ScrollViewEnd"));
var _itemPositions = require("./itemPositions");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const [selectProps, selectedSystemPropTypes] = (0, _utils.selectSystemProps)([_utils.a11yProps, _utils.viewProps]);
const tokenKeys = ['nextIcon', 'previousIcon', 'gutter', 'borderBottomWidth', 'borderBottomColor', 'buttonClearance'];
const selectHorizontalScrollTokens = (themeTokens, prefix) => (0, _utils.selectTokens)(tokenKeys, themeTokens, prefix);
exports.selectHorizontalScrollTokens = selectHorizontalScrollTokens;
const selectBorderStyles = (borderBottomWidth, borderBottomColor) => ({
  borderBottomWidth,
  borderBottomColor
});

/**
 * Base component that scrolls horizontally and displays left and right buttons if
 * there is content to the left and the right which can be scrolled to.
 *
 * @TODO revisit `ScrollButton` after IconButton is stable.
 */
const HorizontalScroll = /*#__PURE__*/_react.default.forwardRef((_ref, ref) => {
  let {
    ScrollButton,
    tokens,
    itemPositions,
    children,
    ...rest
  } = _ref;
  const {
    nextIcon,
    previousIcon,
    gutter,
    borderBottomWidth,
    borderBottomColor,
    buttonClearance = 0
  } = (0, _ThemeProvider.validateThemeTokens)((0, _ThemeProvider.resolveThemeTokens)(tokens),
  // Allow missing icons
  (0, _utils.getTokensSetPropType)(tokenKeys, {
    partial: true
  }), 'HorizontalScroll');
  const [containerWidth, setContainerWidth] = _react.default.useState(0);
  const [contentWidth, setContentWidth] = _react.default.useState(0);
  const handleContentWidth = width => setContentWidth(width);
  const handleContainerLayout = _ref2 => {
    let {
      nativeEvent: {
        layout: {
          width
        }
      }
    } = _ref2;
    return setContainerWidth(width);
  };

  // Update the scroll position only when scrolling actions ends to minimally rerender
  // and update which buttons are conditionally rendered.
  const [scrollOffset, setScrollOffset] = _react.default.useState(0);
  const handleScrollEnd = _ref3 => {
    let {
      nativeEvent: {
        contentOffset: {
          x
        }
      }
    } = _ref3;
    setScrollOffset(x);
  };
  const scrollMax = Math.max(0, contentWidth - containerWidth);
  const showNextButton = scrollOffset < scrollMax;
  const showPreviousButton = scrollOffset > 0;
  const scrollRef = _react.default.useRef(null);
  const scrollTo = targetX => {
    if (typeof scrollRef.current?.scrollTo === 'function') {
      const x = (0, _itemPositions.getItemPositionScrollTarget)(targetX, scrollMax, itemPositions.positions, buttonClearance);
      scrollRef.current.scrollTo({
        x,
        animated: true
      });
      if (_Platform.default.OS === 'android') {
        // React Native calls onScrollMomentumEnd after scrollTo for iOS, but not Android.
        // See https://github.com/facebook/react-native/issues/26661
        // There's also no callback for when scrollTo finishes.
        setTimeout(() => setScrollOffset(x), 300);
      }
    }
  };
  // Make scrollTo and relevant co-ordinates available to any component with an interest in itemPositions.
  // ItemPositions is a mutable ref object intended to have its properties mutated without any rerenders.
  /* eslint-disable-next-line no-param-reassign */
  itemPositions.scrollTo = scrollTo;
  /* eslint-disable-next-line no-param-reassign */
  itemPositions.containerWidth = containerWidth;
  /* eslint-disable-next-line no-param-reassign */
  itemPositions.contentWidth = contentWidth;
  /* eslint-disable-next-line no-param-reassign */
  itemPositions.scrollOffset = scrollOffset;
  const hasWidths = itemPositions.contentWidth > 0;
  _react.default.useEffect(() => {
    if (hasWidths) itemPositions.setIsReady(true);
    // itemPositions is a ref object so this should occur only when elements' widths are set in state
  }, [hasWidths, itemPositions]);

  // When scrolling, allow clearance around scroll buttons, so e.g. this...
  // (<-) Item 5   Item 6   Item 7   Item 8   It(->)
  // ...doesn't scroll like this with Item 9 never being properly visible:
  // (<-)em 9   Item 10  Item 11  Item 12  Item (->)
  const scrollDistance = containerWidth - buttonClearance * 2;
  const scrollNext = () => scrollTo(scrollOffset + scrollDistance);
  const scrollPrevious = () => scrollTo(scrollOffset - scrollDistance);

  // On Web, being scrollable in one direction prevents overflow: visible in the other direction.
  // Apply a fixed margin gutter that tab highlight effects can overhang without getting trimmed.
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_View.default, {
    ref: ref,
    style: staticStyles.container,
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
      style: selectBorderStyles(borderBottomWidth, borderBottomColor),
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_ScrollViewEnd.default, {
        onScrollEnd: handleScrollEnd,
        ref: scrollRef,
        horizontal: true,
        onContentSizeChange: handleContentWidth,
        onLayout: handleContainerLayout,
        showsHorizontalScrollIndicator: false,
        contentContainerStyle: staticStyles.scrollContainer,
        ...selectProps(rest),
        children: children
      })
    }), showPreviousButton && /*#__PURE__*/(0, _jsxRuntime.jsx)(ScrollButton, {
      icon: previousIcon,
      onPress: scrollPrevious,
      offset: gutter,
      direction: "previous"
    }), showNextButton && /*#__PURE__*/(0, _jsxRuntime.jsx)(ScrollButton, {
      icon: nextIcon,
      onPress: scrollNext,
      offset: gutter,
      direction: "next"
    })]
  });
});
HorizontalScroll.displayName = 'HorizontalScroll';
const staticStyles = _StyleSheet.default.create({
  scrollContainer: {
    // Gives inner container width from content, allowing it to exceed its container's width
    alignSelf: 'flex-start',
    flexGrow: 1
  },
  container: {
    // Vertically center the ScrollButtons buttons
    justifyContent: 'center'
  }
});
HorizontalScroll.propTypes = {
  ...selectedSystemPropTypes,
  itemPositions: _itemPositions.itemPositionsPropType,
  ScrollButton: _propTypes.default.elementType,
  tokens: (0, _utils.getTokensSetPropType)(tokenKeys, {
    allowFunction: true
  }),
  children: _propTypes.default.node
};
var _default = exports.default = HorizontalScroll;